/**
 * Frontend Error Reporting Service
 * 
 * Reports frontend errors (React crashes, API errors, etc.) to the backend.
 * Features:
 * - Rate limiting (client-side)
 * - PII sanitization
 * - Queue for offline support
 */

import { apiClient } from './client';

// Error report interface
export interface FrontendErrorReport {
  error_message: string;
  error_type?: string;
  stack_trace?: string;
  url?: string;
  metadata?: Record<string, unknown>;
}

// Queue for offline/error reporting
interface QueuedError {
  report: FrontendErrorReport;
  timestamp: number;
  retryCount: number;
}

const ERROR_QUEUE_KEY = 'kirjuri_error_queue';
const MAX_QUEUE_SIZE = 50;
const MAX_RETRIES = 3;
const RATE_LIMIT_WINDOW_MS = 60 * 1000; // 1 minute
const MAX_ERRORS_PER_WINDOW = 10;

// In-memory rate limiting
let errorCount = 0;
let windowStart = Date.now();

/**
 * Check if we're rate limited
 */
function isRateLimited(): boolean {
  const now = Date.now();
  
  // Reset window if expired
  if (now - windowStart > RATE_LIMIT_WINDOW_MS) {
    errorCount = 0;
    windowStart = now;
    return false;
  }
  
  return errorCount >= MAX_ERRORS_PER_WINDOW;
}

/**
 * Increment error count
 */
function incrementErrorCount(): void {
  errorCount++;
}

/**
 * Get queued errors from localStorage
 */
function getQueuedErrors(): QueuedError[] {
  try {
    const stored = localStorage.getItem(ERROR_QUEUE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Save queued errors to localStorage
 */
function saveQueuedErrors(queue: QueuedError[]): void {
  try {
    // Keep only the most recent errors if queue is too large
    if (queue.length > MAX_QUEUE_SIZE) {
      queue = queue.slice(-MAX_QUEUE_SIZE);
    }
    localStorage.setItem(ERROR_QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Sanitize error message to remove PII
 */
function sanitizeErrorMessage(message: string): string {
  // Remove email addresses
  let sanitized = message.replace(/[\w.-]+@[\w.-]+\.\w+/g, '[EMAIL_REDACTED]');
  // Remove potential tokens (long hex strings)
  sanitized = sanitized.replace(/\b[a-f0-9]{32,}\b/gi, '[TOKEN_REDACTED]');
  return sanitized;
}

/**
 * Sanitize stack trace to remove PII
 */
function sanitizeStackTrace(stack?: string): string | undefined {
  if (!stack) return undefined;
  
  // Remove email addresses
  let sanitized = stack.replace(/[\w.-]+@[\w.-]+\.\w+/g, '[EMAIL_REDACTED]');
  // Remove potential tokens
  sanitized = sanitized.replace(/\b[a-f0-9]{32,}\b/gi, '[TOKEN_REDACTED]');
  // Remove UUIDs
  sanitized = sanitized.replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '[UUID_REDACTED]');
  
  // Limit size
  return sanitized.substring(0, 5000);
}

/**
 * Report a frontend error to the backend
 */
export async function reportFrontendError(
  error: Error | string,
  type: string = 'generic',
  metadata?: Record<string, unknown>
): Promise<void> {
  // Don't report if rate limited
  if (isRateLimited()) {
    console.warn('[ErrorReporter] Rate limited, not reporting error');
    return;
  }
  
  const errorMessage = typeof error === 'string' ? error : error.message;
  const stackTrace = typeof error === 'string' ? undefined : error.stack;
  
  const report: FrontendErrorReport = {
    error_message: sanitizeErrorMessage(errorMessage),
    error_type: type,
    stack_trace: sanitizeStackTrace(stackTrace),
    url: window.location.href,
    metadata: {
      ...metadata,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    },
  };
  
  // Try to send immediately
  try {
    await sendErrorReport(report);
    incrementErrorCount();
  } catch {
    // Queue for later if failed
    queueError(report);
  }
}

/**
 * Send error report to backend
 */
async function sendErrorReport(report: FrontendErrorReport): Promise<void> {
  await apiClient.post('/logs/frontend-errors', report, { retry: false });
}

/**
 * Queue an error for later reporting
 */
function queueError(report: FrontendErrorReport): void {
  const queue = getQueuedErrors();
  queue.push({
    report,
    timestamp: Date.now(),
    retryCount: 0,
  });
  saveQueuedErrors(queue);
}

/**
 * Process queued errors (call this on app initialization)
 */
export async function processQueuedErrors(): Promise<void> {
  const queue = getQueuedErrors();
  if (queue.length === 0) return;
  
  const remaining: QueuedError[] = [];
  
  for (const item of queue) {
    // Skip if too many retries
    if (item.retryCount >= MAX_RETRIES) {
      continue;
    }
    
    // Skip old errors (older than 7 days)
    if (Date.now() - item.timestamp > 7 * 24 * 60 * 60 * 1000) {
      continue;
    }
    
    try {
      await sendErrorReport(item.report);
      incrementErrorCount();
      // Successfully sent, don't add to remaining
    } catch {
      // Failed, increment retry and keep in queue
      remaining.push({
        ...item,
        retryCount: item.retryCount + 1,
      });
    }
  }
  
  saveQueuedErrors(remaining);
}

/**
 * Report an API error
 */
export function reportApiError(
  status: number,
  statusText: string,
  url: string,
  responseData?: unknown
): void {
  // Don't report 401/403 (expected auth errors) or 429 (rate limited — reporting
  // it would trigger another request to the same rate-limited endpoint)
  if (status === 401 || status === 403 || status === 429) return;
  
  // Don't report validation errors (400) unless unexpected
  if (status === 400 && responseData && typeof responseData === 'object') {
    // Check if it's a validation error (has validationErrors field)
    const data = responseData as Record<string, unknown>;
    if (data.validationErrors || data.fieldErrors) return;
  }
  
  const errorType = status >= 500 ? 'api_server_error' : 'api_client_error';
  
  reportFrontendError(
    `API Error ${status}: ${statusText}`,
    errorType,
    {
      apiUrl: url,
      status,
      responseData: responseData ? JSON.stringify(responseData).substring(0, 1000) : undefined,
    }
  );
}

/**
 * Report a React error (from Error Boundary)
 */
export function reportReactError(error: Error, componentStack?: string): void {
  reportFrontendError(
    error,
    'react_error_boundary',
    {
      componentStack: componentStack?.substring(0, 2000),
    }
  );
}

// Initialize: process any queued errors on load
if (typeof window !== 'undefined') {
  // Process queued errors after a short delay to not block initial load
  setTimeout(processQueuedErrors, 5000);
}
